import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, Loader2, Trash2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, type Student, type TenantVerticalLiteral } from "@eduviaos/shared";
import { useDeleteStudent } from "@/hooks/useStudents";
import { useTerms } from "@/hooks/useTerms";

/**
 * Phase J1.b — Student soft-delete confirm dialog.
 *
 * The BE performs a soft-delete (sets `deleted_at`), so technically the
 * student row stays in the DB and is recoverable by a DBA. The UI still
 * treats this like a destructive action — the admin must type the
 * student's name to confirm. Mirrors the destructive-confirm UX from
 * the SA-side `<DeleteTenantDialog>`.
 */

export interface StudentDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
  onDeleted?: (studentId: string) => void;
  vertical?: TenantVerticalLiteral;
}

export function StudentDeleteDialog({
  open,
  onOpenChange,
  student,
  onDeleted,
  vertical,
}: StudentDeleteDialogProps) {
  const { terms } = useTerms(vertical);
  const personOne = terms.learner.one.toLowerCase();
  const reduceMotion = useReducedMotion();
  const mutation = useDeleteStudent();

  const [confirmText, setConfirmText] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setConfirmText("");
      setSubmitError(null);
    }
  }, [open]);

  const expected = student?.name?.trim() ?? "";
  const canSubmit = expected.length > 0 && confirmText.trim() === expected && !mutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit || !student) return;
    try {
      await mutation.mutateAsync(student.id);
      onDeleted?.(student.id);
      // Phase E1.b.2 — global toast on success; in-dialog
      // `role="alert"` banner kept for failures (the admin needs the
      // error visible while still inside the destructive-confirm
      // flow so they can retry without re-typing the student name).
      toast.success(`Archived ${student.name}.`);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || `Couldn't remove this ${personOne}.`
          : err instanceof Error
            ? err.message
            : `Couldn't remove this ${personOne}.`;
      setSubmitError(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="student-delete-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-destructive/10 text-destructive mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Trash2 className="size-5" />
            </span>
            <div className="flex-1">
              <DialogTitle className="tracking-tight">Remove {personOne}?</DialogTitle>
              <DialogDescription className="mt-1 text-xs">
                <strong>{student?.name ?? `This ${personOne}`}</strong> will be removed from rosters
                and reports. The record is kept, so support can restore it if this was a mistake.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="student-delete-form">
          <AnimatePresence>
            {submitError ? (
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span>{submitError}</span>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div>
            <Label htmlFor="student-delete-confirm" className="text-xs">
              Type <code className="text-foreground">{expected}</code> to confirm.
            </Label>
            <Input
              id="student-delete-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              autoFocus
              className="mt-1"
              placeholder={expected}
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={!canSubmit}>
              {mutation.isPending ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Remove {personOne}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
